import { CoreFieldProps, fieldTypes } from "@ucl/library";
import { FC } from "react";
import { FieldComplexV2 } from "./FieldComplexV2";
import { COMPLEX_FIELD_KEYS } from "@/constants/fieldKeys";
import "./ComplexFields.scss";
import { OrganizationAccountAcceptableUse } from "@/types/OrganizationAccountAcceptableUse";
import { FinancialApplication } from "@/types";

export interface OrganizationAccountAcceptableUseComplexValue {
  showHeader?: boolean;
  organizationName?: string;
}

export interface OrganizationAccountAcceptableUseComplexFieldProps
  extends fieldTypes.BaseFieldProps<OrganizationAccountAcceptableUse> {
  formValue?: FinancialApplication;
}

export const OrganizationAccountAcceptableUseComplexField: FC<
  OrganizationAccountAcceptableUseComplexFieldProps
> = (props) => {
  const organizationAccountAcceptableUseComplexValue =
    props.complexFieldValue as OrganizationAccountAcceptableUseComplexValue;
  const organizationName =
    organizationAccountAcceptableUseComplexValue?.organizationName || "your organization";

  // Check if prohibited activities is true to conditionally show sub-questions
  const hasProhibitedActivities =
    props.formValue?.organizationAccountAcceptableUse?.prohibitedActivities === true;

  const PROHIBITED_ACTIVITIES_BULLETS = [
    "The storage of valued cards",
    "Gambling, internet gambling, or gaming machines",
    "The sale or redemption of lottery tickets, money orders, or travelers checks",
    "Adult entertainment or the sale of firearms",
    "The marijuana industry or the servicing of the marijuana industry",
    "Cannabis-related investments or real estate leases",
    "Illegal substances (including, but not limited to: cannabis, CBD, hemp)",
    "Cryptocurrency exchanges or penny auctions",
    "Cryptocurrency activities or NFT-related activities",
    "The use or acceptance of virtual currency",
    "Defense contractors or ITAR-regulated entities",
    "Privately-owned ATMs",
    "Cashing checks greater than $1,000 per customer per day",
    "An initial deposit of $3,000 or greater in cash, deposited at a physical branch location",
  ];

  return (
    <section className="complex-field-container organization-account-acceptable-use">
      {organizationAccountAcceptableUseComplexValue?.showHeader && <h4>{props.label}</h4>}
      <div className="complex-field-content">
        {/* <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.SEND_RECEIVE_WIRES
          }
          complexFieldProps={
            {
              label: (
                <>
                  Will <b>{organizationName}</b> send or receive wires?
                </>
              ),
              showClearButton: true,
            } as Partial<CoreFieldProps>
          }
        /> */}
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.PROHIBITED_ACTIVITIES
          }
          complexFieldProps={
            {
              label: (
                <>
                  Will <b>{organizationName}</b> have or be involved in any of the following?
                </>
              ),
              showClearButton: true,
            } as Partial<CoreFieldProps>
          }
        />
        {/* Show bullet point list when prohibited activities is not "Yes" */}
        {!hasProhibitedActivities && (
          <div className="prohibited-activities-list">
            <ul>
              {PROHIBITED_ACTIVITIES_BULLETS.map((bullet, index) => (
                <li key={index}>{bullet}</li>
              ))}
            </ul>
          </div>
        )}
        {hasProhibitedActivities && (
          <>
            <div className="prohibited-activities-table-header">
              Please indicate your involvement with each of the following activities:
            </div>
            <div className="prohibited-activities-table">
              <div className="prohibited-activities-table-header-row">
                <div className="prohibited-activities-table-header-activities-cell">Activities</div>
                <div className="prohibited-activities-table-header-yes-no-cell">Yes / No</div>
              </div>
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.STORAGE_OF_VALUED_CARDS
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.GAMBLING}
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.LOTTERY_TICKETS
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE
                    .ADULT_ENTERTAINMENT_FIREARMS
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.MARIJUANA_INDUSTRY
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.CANNABIS_INVESTMENTS
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.ILLEGAL_SUBSTANCES
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.CRYPTO_EXCHANGES
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.CRYPTO_ACTIVITIES
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.VIRTUAL_CURRENCY
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.PRIVATE_ATMS
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
              <FieldComplexV2
                {...props}
                complexFieldKey={
                  COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.INITIAL_CASH_DEPOSIT
                }
                complexFieldProps={
                  {
                    showClearButton: true,
                  } as Partial<CoreFieldProps>
                }
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
};
